import type { Rappen } from './money.js'

/**
 * Zahlungsstatus.
 *
 * Abgeleitet, nicht gespeichert – aus demselben Grund wie «verrechnet» beim
 * Rapport und «gesperrt» beim Beleg: ein gespeicherter Merker müsste bei jeder
 * Zahlung, jeder Stornierung und jeder Betragsänderung nachgezogen werden, und
 * an einer dieser Stellen wäre er irgendwann falsch. Beim Geld ist «irgendwann
 * falsch» die teuerste Art von Fehler.
 *
 * Die Regel steht hier und nirgends sonst. Server und Oberfläche rechnen mit
 * derselben Funktion; gäbe es zwei, würden sie irgendwann verschiedene Antworten
 * geben, und der Anwender sähe auf dem Bildschirm etwas anderes als in der
 * Datenbank.
 */

export type ZahlungsStatus = 'offen' | 'teilbezahlt' | 'bezahlt' | 'ueberbezahlt' | 'storniert'

export const ZAHLUNGS_STATUS_TEXT: Record<ZahlungsStatus, string> = {
  offen: 'offen',
  teilbezahlt: 'teilbezahlt',
  bezahlt: 'bezahlt',
  ueberbezahlt: 'überbezahlt',
  storniert: 'storniert'
}

export interface Zahlungsstand {
  totalRp: Rappen
  paidRp: Rappen
  /** Was noch fehlt. Nie negativ – bei Überzahlung steht die Differenz in overpaidRp. */
  openRp: Rappen
  /** Was zu viel bezahlt wurde. Nie negativ. */
  overpaidRp: Rappen
  status: ZahlungsStatus
}

/**
 * Zahlungsstand einer Rechnung.
 *
 * `documentCancelled` kommt vom Belegzustand und nicht aus den Zahlungen: eine
 * stornierte Rechnung ist weder offen noch bezahlt, sie ist gegenstandslos.
 * Würde man sie als «offen» führen, stünde sie für immer in der Arbeitsliste.
 *
 * Die Rundung ist kein Thema, weil alles ganze Rappen sind. Genau dafür gibt es
 * diese Einheit im ganzen Programm.
 */
export function zahlungsstand(input: {
  totalRp: Rappen
  paidRp: Rappen
  documentCancelled?: boolean
}): Zahlungsstand {
  const totalRp = Math.round(input.totalRp)
  const paidRp = Math.round(input.paidRp)
  const differenz = totalRp - paidRp

  const status: ZahlungsStatus = input.documentCancelled
    ? 'storniert'
    : paidRp <= 0
      ? 'offen'
      : differenz > 0
        ? 'teilbezahlt'
        : differenz === 0
          ? 'bezahlt'
          : 'ueberbezahlt'

  /*
    Eine stornierte Rechnung fordert nichts mehr.

    `openRp` stand hier bisher unabhängig vom Storno auf der Differenz – der
    Status wurde auf «storniert» gesetzt, die offene Summe aber blieb stehen.
    Jede Auswertung, die auf `openRp` addiert statt auf den Status zu schauen,
    zählte damit stornierte Beträge als offene Forderung mit; im Kundenblatt
    (office.ts, `summe.offenRp`) und im Projektblatt geschah genau das. Der
    Betrieb sah Geld ausstehen, das er nie bekommen wird und auch nicht
    verlangt.

    Der Kommentar oben sagt es bereits: «weder offen noch bezahlt, sie ist
    gegenstandslos». Diese Zeilen ziehen die Rechnung nach.
  */
  return {
    totalRp,
    paidRp,
    openRp: input.documentCancelled ? 0 : Math.max(0, differenz),
    overpaidRp: input.documentCancelled ? 0 : Math.max(0, -differenz),
    status
  }
}

/**
 * Belegzustände, in denen der Beleg den Betrieb nachweislich nicht verlassen
 * hat.
 *
 * `versand_unklar` steht bewusst NICHT hier: dort wurde gesendet und niemand
 * weiss, was ankam. Diesen Fall als «nicht draussen» zu führen hiesse, offenes
 * Geld kleinzurechnen – und er steht ohnehin als dringendster Punkt auf der
 * Arbeitsliste, verschwindet also nicht.
 *
 * `versand_fehlgeschlagen` steht hier, weil dort bekannt ist, dass nichts
 * ankam.
 */
export const NICHT_DRAUSSEN_ZUSTAENDE = [
  'entwurf',
  'wird_abgeschlossen',
  'nummer_ohne_pdf',
  'bereit',
  'versand_fehlgeschlagen'
] as const

const NICHT_DRAUSSEN: ReadonlySet<string> = new Set(NICHT_DRAUSSEN_ZUSTAENDE)

/**
 * Hat dieser Beleg den Betrieb verlassen?
 *
 * Der Belegzustand allein genügt nicht. Der Weg über
 * `POST /documents/:id/versand` setzt nur einen Zeitstempel und lässt den
 * Zustand auf `bereit` stehen – eine so versendete Rechnung sähe sonst für
 * immer aus wie eine, die nie hinausging.
 *
 * Auch `gesendet_an_firma_am` und `gesendet_an_benutzer_am` zählen. Wer sich
 * die Rechnung selbst schickt, druckt sie und legt sie in die Post; für die
 * Frage «kann der Kunde davon wissen» ist das dasselbe.
 *
 * Und `versendetVermerktAm`: der Fall, in dem Raporto gar nichts gesendet hat
 * und ein Mensch bestätigt, dass die Rechnung trotzdem draussen ist – gedruckt
 * und in die Post, übergeben, von Hand zugestellt. Ohne diesen Weg bliebe jeder
 * Betrieb, der auf Papier verschickt, dauerhaft ohne Mahnwesen.
 */
export function istDraussen(beleg: {
  status: string
  gesendetAnKundeAm?: string | Date | null
  gesendetAnFirmaAm?: string | Date | null
  gesendetAnBenutzerAm?: string | Date | null
  versendetVermerktAm?: string | Date | null
}): boolean {
  if (
    beleg.gesendetAnKundeAm ||
    beleg.gesendetAnFirmaAm ||
    beleg.gesendetAnBenutzerAm ||
    beleg.versendetVermerktAm
  ) {
    return true
  }
  return !NICHT_DRAUSSEN.has(beleg.status)
}

/**
 * Der Stichtag als «JJJJ-MM-TT».
 *
 * Eine Zeichenkette ist der Geschäftstag, den der Aufrufer bereits kennt – und
 * genau den soll er übergeben. Ein `Date` wird als UTC gelesen; das ist die
 * alte Bedeutung und bleibt nur als Rückfall stehen, damit kein Aufrufer
 * stillschweigend etwas anderes bekommt als bisher.
 */
function stichtag(heute: string | Date): string {
  return typeof heute === 'string' ? heute.slice(0, 10) : heute.toISOString().slice(0, 10)
}

/**
 * Ist diese Rechnung überfällig?
 *
 * Überfällig heisst dreierlei: die Zahlungsfrist ist vorbei, es fehlt noch
 * Geld – und die Rechnung ist beim Kunden. Eine bezahlte Rechnung wird nie
 * überfällig, auch wenn das Datum längst durch ist; eine ohne
 * Fälligkeitsdatum ebenfalls nicht, weil dann niemand etwas versprochen hat.
 *
 * **Der dritte Punkt ist neu.** Vorher zählte jede abgeschlossene Rechnung
 * nach Fristablauf als überfällig, auch eine, die nie hinausging. Ein Kunde,
 * der keine Rechnung bekommen hat, ist nicht säumig – und der Betrieb, der
 * ihn deshalb mahnt, blamiert sich. Die nie versendete Rechnung verschwindet
 * dadurch nicht: sie steht als eigener, dringender Punkt in der Arbeitsliste
 * («Abgeschlossen, aber noch nicht versendet») und mit ihrem vollen Betrag
 * unter den offenen Forderungen.
 *
 * `heute` ist der GESCHÄFTSTAG, nicht der UTC-Tag. Fälligkeitsdaten entstehen
 * mit CURRENT_DATE in der Zeitzone der Datenbank; wer hier UTC vergleicht,
 * bekommt zwischen Mitternacht und zwei Uhr früh einen Tag Unterschied – und
 * genau in diesem Fenster zählte eine gestern abgelaufene Rechnung noch nicht
 * als überfällig.
 */
export function istUeberfaellig(
  dueDate: string | null | undefined,
  stand: Zahlungsstand,
  heute: string | Date = new Date(),
  /*
    Ohne Angabe wie bisher: der Beleg gilt als draussen. So bleibt ein
    Aufrufer, der den Belegzustand gar nicht zur Hand hat, bei der alten
    Antwort, statt still auf «nie überfällig» zu kippen.
  */
  draussen = true
): boolean {
  if (!dueDate) return false
  if (stand.status !== 'offen' && stand.status !== 'teilbezahlt') return false
  if (!draussen) return false
  return dueDate.slice(0, 10) < stichtag(heute)
}

/** Wie viele Tage überfällig? Für die Reihenfolge in der Arbeitsliste. */
export function tageUeberfaellig(
  dueDate: string | null | undefined,
  heute: string | Date = new Date()
): number {
  if (!dueDate) return 0
  const faellig = Date.parse(`${dueDate.slice(0, 10)}T00:00:00Z`)
  const jetzt = Date.parse(`${stichtag(heute)}T00:00:00Z`)
  return Math.max(0, Math.round((jetzt - faellig) / 86_400_000))
}
